import { api } from "@/modules/api";
import { logger } from "@/modules/logger";
import type { Uuid } from "@/domains/global/identifiers";

const DEFAULT_UPDATE_TRANSCRIPT_ERROR_MESSAGE =
  "Update transcript request failed. Please try again.";

type UpdateAudioRecordingTranscriptArgs = {
  audioRecordingId: Uuid;
  transcriptText: string;
};

type UpdateAudioRecordingTranscriptResult =
  | { success: true }
  | { success: false; errorMessage: string };

const hasTranscriptText = (transcriptText: string): boolean =>
  typeof transcriptText === "string" && transcriptText.trim().length > 0;

const extractUpdateTranscriptErrorMessage = (rawError: unknown): string => {
  if (!rawError || typeof rawError !== "object") {
    return DEFAULT_UPDATE_TRANSCRIPT_ERROR_MESSAGE;
  }

  const errorWithMessage = rawError as { error_message?: unknown; detail?: unknown };

  if (typeof errorWithMessage.error_message === "string") {
    return errorWithMessage.error_message;
  }

  if (typeof errorWithMessage.detail === "string") {
    return errorWithMessage.detail;
  }

  return DEFAULT_UPDATE_TRANSCRIPT_ERROR_MESSAGE;
};

export async function updateAudioRecordingTranscript({
  audioRecordingId,
  transcriptText,
}: UpdateAudioRecordingTranscriptArgs): Promise<UpdateAudioRecordingTranscriptResult> {
  if (!hasTranscriptText(transcriptText)) {
    return {
      success: false,
      errorMessage: "Transcript content is required.",
    };
  }

  try {
    const response = await api.post("/v2/audio-recordings/{audio_recording_id}/update-transcript", {
      params: { path: { audio_recording_id: audioRecordingId } },
      body: { transcript_text: transcriptText },
    });

    if (!response.response.ok) {
      const errorMessage = extractUpdateTranscriptErrorMessage(response.error);
      logger.warn({
        message: "[updateAudioRecordingTranscript] API call failed",
        info: { audioRecordingId, errorMessage },
      });
      return { success: false, errorMessage };
    }

    return { success: true };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : DEFAULT_UPDATE_TRANSCRIPT_ERROR_MESSAGE;
    logger.error({
      message: "[updateAudioRecordingTranscript] Request failed",
      info: { audioRecordingId, error: String(error) },
    });
    return { success: false, errorMessage };
  }
}
